// Mui Components
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Theme,
    Button,
    ButtonProps,
    IconButton,
    SxProps,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import CloseIcon from "@mui/icons-material/Close";
import theme from "assets/styles/themes";

const cardBackground = {
    default: theme.palette.background.blue,
    primary: theme.palette.common.white,
};

type CardMessageProp = { color: keyof typeof cardBackground; hideHeaderBg: boolean };

const useStyles = makeStyles<Theme, CardMessageProp>((theme: Theme) => {
    return {
        cardMessageHeader: {
            background: ({ hideHeaderBg, color }) =>
                hideHeaderBg
                    ? cardBackground[color]
                    : `linear-gradient(90deg, ${theme.palette.gradients.g06.n1} 0.85%, ${theme.palette.gradients.g06.n2} 100.85%)`,
            paddingBottom: ({ hideHeaderBg }) => theme.spacing(hideHeaderBg ? 0 : 2),
            "& .MuiCardHeader-title": {
                fontWeight: 600,
                fontSize: 17,
                color: ({ hideHeaderBg }) => (hideHeaderBg ? theme.palette.primary.main : theme.palette.common.white),
            },
            "& .MuiSvgIcon-root": {
                color: ({ hideHeaderBg }) => (hideHeaderBg ? theme.palette.primary.main : theme.palette.common.white),
            },
        },
        cardMessageRoot: {
            maxWidth: 650,
            fontSize: 12,
            boxShadow: "0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)",
            borderRadius: 6,
            margin: "0 auto",

            "& .MuiCardContent-root": {
                backgroundColor: ({ color }) => cardBackground[color],
                padding: theme.spacing(1.5),
                "& p": {
                    fontSize: 14,
                    "&:first-child": { marginTop: 0 },
                    "&:last-child": { marginBottom: 0 },
                },
            },
            "& .MuiCardActions-root": {
                backgroundColor: ({ color }) => cardBackground[color],
                padding: theme.spacing(0, 2, 2),
                justifyContent: "flex-end",
                "& .MuiButton-root": {
                    display: "flex",
                    alignItems: "center",
                    minWidth: "initial",
                    marginLeft: theme.spacing(1),
                },
            },
        },
    };
});

export type TCardMessageProps = {
    title?: React.ReactNode;
    hideHeaderBg?: boolean;
    color?: "default" | "primary";
    closeButtonAction?: () => void;
    content: string | React.ReactNode;
    showCloseButton?: boolean;
    actions?: {
        startIcon?: JSX.Element;
        endIcon?: JSX.Element;
        icon?: JSX.Element;
        label?: string | JSX.Element;
        onClickAction: () => void;
        buttonProps?: ButtonProps;
    }[];
    sx?: SxProps<Theme>;
};

export const CardMessageWrapper = ({ children, sx }: { children: JSX.Element; sx?: SxProps<Theme> }) => {
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
                padding: theme.spacing(3),
                ...sx,
            }}
        >
            {children}
        </Box>
    );
};

export const CardMessage = ({
    title,
    content,
    color = "default",
    hideHeaderBg = false,
    closeButtonAction,
    actions,
    ...sx
}: TCardMessageProps) => {
    const classes = useStyles({ color, hideHeaderBg });

    return (
        <Card className={classes.cardMessageRoot} {...sx}>
            {title && (
                <CardHeader
                    title={title}
                    className={classes.cardMessageHeader}
                    {...(closeButtonAction
                        ? {
                              action: (
                                  <IconButton onClick={closeButtonAction}>
                                      <CloseIcon />
                                  </IconButton>
                              ),
                          }
                        : {})}
                />
            )}
            <CardContent>{content}</CardContent>
            {actions?.length ? (
                <CardActions disableSpacing>
                    {actions.map(({ label, startIcon, endIcon, onClickAction, buttonProps }, i) => (
                        <Button
                            key={i}
                            onClick={onClickAction}
                            variant="contained"
                            startIcon={startIcon}
                            endIcon={endIcon}
                            {...buttonProps}
                        >
                            {/* {startIcon && startIcon} */}
                            {label && <Box>{label}</Box>}
                            {/* {endIcon && endIcon} */}
                        </Button>
                    ))}
                </CardActions>
            ) : null}
        </Card>
    );
};
